import { Component, computed, DestroyRef, effect, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyStickyFooterModule } from '@vendasta/galaxy/sticky-footer';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BrandStatus } from '@vendasta/smsv2';
import { catchError, combineLatest, map, of, take } from 'rxjs';
import { SMSI18NModule } from '../../../assets/i18n/sms-i18n.module';
import { BusinessCategory, BusinessType, Position, Region } from '../sms-registration-form/types';
import { BusinessCategoryToLabelPipe } from '../sms-registration-form/pipes/business-category-to-label.pipe';
import { RegionToLabelPipe } from '../sms-registration-form/pipes/region-to-label.pipe';
import { ConfirmationModalComponent } from '../sms-registration-form/comfirmation-dialog/confirmation-modal.component';
import { A2PRegistrationService } from '../a2p-registration.service';
import { RegistrationStage } from '../a2p-registration.constants';
import { SmsPhoneInputComponent } from '../../../components/phone-input/sms-phone-input.component';
import { SmsContextToken, SmsInjectionToken } from '../../../token';
import { RegistrationForm } from './sms-registration.form';

@Component({
  selector: 'sms-registration-form-v2',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatStepperModule,
    GalaxyAlertModule,
    GalaxyFormFieldModule,
    GalaxyStickyFooterModule,
    BusinessCategoryToLabelPipe,
    RegionToLabelPipe,
    TranslateModule,
    SMSI18NModule,
    SmsPhoneInputComponent,
    DatePipe,
  ],
  templateUrl: './sms-registration-form-v2.component.html',
  styleUrl: './sms-registration-form-v2.component.scss',
})
export class SmsRegistrationFormV2Component {
  readonly stepper = viewChild(MatStepper);

  protected readonly BusinessCategories = Object.values(BusinessCategory);
  protected readonly BusinessTypes = Object.values(BusinessType);
  protected readonly Regions = Object.values(Region);
  protected readonly JobPositions = Object.values(Position);

  readonly totalSteps = 3;
  readonly currentStep = signal(0);
  readonly isFirstStep = computed(() => this.currentStep() === 0);
  readonly isLastStep = computed(() => this.currentStep() === this.totalSteps - 1);

  readonly formLocked = signal(false);
  readonly isCampaignErrorState = signal(false);
  private lastOwnerId: string | null = null;

  private readonly smsContext = inject(SmsContextToken);
  private readonly sendsViaBroadly = toSignal(this.smsContext.sendsViaBroadly$, { initialValue: false });
  readonly submittedDate = computed(() => (this.sendsViaBroadly() ? undefined : this.registration()?.info?.submitted));
  readonly isRegistrationComplete = computed(
    () => this.registration()?.stage === RegistrationStage.RegistrationComplete,
  );
  readonly isBrandApproved = computed(
    () => this.registration()?.status?.brand?.status === BrandStatus.BRAND_STATUS_APPROVED,
  );
  readonly isDataErrorState = computed(() => this.registration()?.stage === RegistrationStage.DataError);

  private readonly smsRegistrationService = inject(A2PRegistrationService);
  private readonly smsDeps = inject(SmsInjectionToken);
  private readonly snackbarService = inject(SnackbarService);
  private readonly modal = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);

  readonly isA2pSubmitRestricted = toSignal(this.smsDeps.isA2pSubmitRestricted$ ?? of(false), { initialValue: false });

  readonly smsForm = new RegistrationForm();
  readonly brandForm = this.smsForm.brand;
  readonly campaignForm = this.smsForm.campaign;
  readonly phoneInputFormGroup = this.smsForm.phoneInput;

  get brandSummary() {
    return this.brandForm.getRawValue();
  }
  get campaignSummary() {
    return this.campaignForm.getRawValue();
  }

  readonly registration = toSignal(
    combineLatest([
      this.smsRegistrationService.a2PRegistrationInfo$,
      this.smsRegistrationService.registrationStage$,
      this.smsRegistrationService.a2pRegistrationStatus$,
      this.smsRegistrationService.getPrefillA2PFormInfo(),
      this.smsRegistrationService.stateOwnerId$,
    ]).pipe(
      map(([info, stage, status, prefill, ownerId]) => ({ info, stage, status, prefill, ownerId })),
      catchError((error) => {
        if (error.status !== 404) {
          this.snackbarService.openErrorSnack(
            this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.FETCH_ERROR'),
          );
        }
        return of(undefined);
      }),
    ),
  );

  constructor() {
    this.smsForm.setupBehaviors(this.destroyRef, this.translateService);

    effect(() => {
      const data = this.registration();
      if (!data?.info) return;

      const ownerChanged = data.ownerId !== this.lastOwnerId;
      if (ownerChanged) {
        this.lastOwnerId = data.ownerId;
        this.smsForm.resetAndPrefill(data.prefill, this.translateService);
      }

      const { locked, campaignError } = this.smsForm.applyRegistrationStage(data.stage, data.info, data.status);

      this.formLocked.set(locked);
      this.isCampaignErrorState.set(campaignError);

      if (locked) {
        this.currentStep.set(this.totalSteps - 1);
      } else if (this.isBrandApproved() && campaignError) {
        this.currentStep.set(1);
      } else if (this.isDataErrorState()) {
        this.navigateToFirstErrorStep();
      }
    });
  }

  onStepChange(event: { selectedIndex: number }): void {
    this.currentStep.set(event.selectedIndex);
  }

  nextStep(): void {
    this.stepper()?.next();
  }

  prevStep(): void {
    this.stepper()?.previous();
  }

  submitRegistrationForm(): void {
    if (this.smsForm.invalid) {
      this.snackbarService.openErrorSnack(
        this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.INVALID_SUBMISSION'),
      );
      this.navigateToFirstErrorStep();
      return;
    }

    this.modal
      .open(ConfirmationModalComponent, { width: '420px' })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.upsertData(true);
        }
      });
  }

  saveChanges(): void {
    if (this.smsForm.hasFormatErrors()) {
      this.snackbarService.openErrorSnack(
        this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.INVALID_FORMAT'),
      );
      return;
    }

    this.upsertData(false);
  }

  private upsertData(isSubmitted: boolean): void {
    const request = this.smsForm.toUpsertRequest(isSubmitted);

    this.smsRegistrationService
      .upsertA2pRegistration(request)
      .then(() => {
        this.snackbarService.openSuccessSnack(
          this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.SAVE_SUCCESS'),
        );
        if (isSubmitted) {
          this.formLocked.set(true);
          this.smsForm.lockAll();
        }
      })
      .catch(() => {
        this.snackbarService.openErrorSnack(this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.SAVE_ERROR'));
      });
  }

  private navigateToFirstErrorStep(): void {
    const step = this.smsForm.findFirstErrorStep();
    if (step !== undefined && step !== this.currentStep()) {
      const stepperRef = this.stepper();
      if (stepperRef) {
        stepperRef.selectedIndex = step;
      }
      this.currentStep.set(step);
    }
  }
}
